import { useTranslations } from '@vocab/react';
import {
  IconSocialFacebook,
  IconSocialLinkedIn,
  Inline,
  Dialog,
  Link,
  IconSocialX,
} from 'braid-design-system';
import type React from 'react';

import { useConfig } from 'src/hooks/useConfig';
import type { ConfigUtils, Job } from 'src/types';

import translations from './.vocab';
import { IconCopyAction } from './IconCopyAction';
import {
  createFacebookShareLink,
  createJobAdPath,
  createLinkedInShareLink,
  createTwitterShareLink,
} from './links';

interface ShareJobDialogProps {
  job: Job;
  open: boolean;
  onClose(): void;
}

const getJobAdUrl = (
  job: Job,
  ref: string,
  urlResolver: ConfigUtils['urlResolver'],
) =>
  urlResolver({
    path: createJobAdPath(job, ref),
    product: 'candidate',
  });

export const ShareJobDialog: React.FC<
  React.PropsWithChildren<ShareJobDialogProps>
> = ({ job, open, onClose }) => {
  const { site, urlResolver } = useConfig();
  const { t } = useTranslations(translations);

  return (
    <Dialog
      id="share-job"
      title={t('Share job')}
      open={open}
      onClose={() => onClose()}
    >
      <Inline space="medium" alignY="center">
        <Link
          href={createFacebookShareLink(
            getJobAdUrl(job, 'fb-share-hirer-jobs-list', urlResolver),
          )}
          target="_blank"
        >
          <IconSocialFacebook
            size="large"
            titleId={`${job.jobId}-facebook`}
            title={t('Share job in facebook')}
          />
        </Link>
        <Link
          href={createTwitterShareLink({
            jobAdUrl: getJobAdUrl(
              job,
              'twitter-share-hirer-jobs-list',
              urlResolver,
            ),
            jobTitle: job.title,
            site,
          })}
          target="_blank"
        >
          <IconSocialX
            size="large"
            titleId={`${job.jobId}-twitter`}
            title={t('Share job in twitter')}
          />
        </Link>
        <Link
          href={createLinkedInShareLink(
            getJobAdUrl(job, 'linkedin-share-hirer-jobs-list', urlResolver),
          )}
          target="_blank"
        >
          <IconSocialLinkedIn
            size="large"
            titleId={`${job.jobId}-linked-in`}
            title={t('Share job in linked in')}
          />
        </Link>
        <IconCopyAction job={job} />
      </Inline>
    </Dialog>
  );
};
